'use client';
import type { LocalInfoType } from '@/apis/common.types';
import JDIPrice from '@/components/price/index';
import { dataExpTagConfig } from '@/components/product/const';
import RichComponent from '@/components/product/richComponent';
import ArrowIcon from '@/images/icon/arrow-default.svg';
import { useStores } from '@/models';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/25217';
import { Checkbox } from '@mui/material';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { type ServiceFloorData, type ServiceItem, CheckTypeEnum, EnableStatus } from '../interface';
import ServiceDetailDraw from '../serviceDetailDraw';
import styles from './index.module.scss';
import { styled } from '@mui/material/styles';

const BpIcon = styled('span')({
  borderRadius: 4,
  width: 20,
  height: 20,
  margin: '0px 2px',
  border: '1px solid #888b94',
  backgroundColor: '#ffffff',
  'input:hover ~ &': {
    backgroundColor: '#f0f1f4',
  },
  'input:active ~ &': {
    backgroundColor: '#e6e8ed',
  },
});
interface ServiceListProps {
  serviceData: ServiceFloorData;
  isDoubleCheck?: boolean; // 是否二次确认弹出
  wrapperClassName?: string;
  useShareCheck?: boolean;
  mainSkuId: string;
  localInfo: LocalInfoType;
}

const ServiceList = ({
  serviceData,
  isDoubleCheck,
  wrapperClassName,
  useShareCheck,
  mainSkuId,
  localInfo,
}: ServiceListProps) => {
  // const hasInitialized = useRef(false);
  const [detailConfig, setDetailConfig] = useState({});

  const { serviceListStore } = useStores();
  const { selectedList, setMainSkuIdAndSelectedList } = serviceListStore;

  const setSelectedList = useCallback(
    ({ selectedList, useShareCheck }: { selectedList: ServiceItem[]; useShareCheck?: boolean }) => {
      const selectedValues = selectedList?.map((i) => i.skuId).join(',');
      if (useShareCheck) {
        const url = new URL(window.location.href);
        if (selectedValues) {
          url.searchParams.set('service_selected', selectedValues);
        } else {
          url.searchParams.delete('service_selected');
        } // 只修改地址，不跳转
        window.history.replaceState(null, '', url.toString());
      }
      setMainSkuIdAndSelectedList(mainSkuId, selectedList);
    },
    [setMainSkuIdAndSelectedList, mainSkuId],
  );

  useEffect(() => {
    if (useShareCheck) {
      const params = new URLSearchParams(window.location.search);
      const selectedValuesStore = params?.get('service_selected')?.split(',') || [];
      // const selectedValuesStore = cookieUtils
      //   ?.getCookie(`${mainProductSkuId}_service_selected`)
      //   ?.split(",");
      if (selectedValuesStore.length) {
        const newSelectedList =
          serviceData?.serviceItems?.filter(
            (item) => item?.skuId && selectedValuesStore.includes(item.skuId),
          ) || [];
        setSelectedList({ selectedList: newSelectedList, useShareCheck });
      }
    } else {
      // 从服务端数据获取的默认值
      const defaultDataSelectedList = serviceData?.serviceItems?.filter(
        (item) => item?.checkType === CheckTypeEnum.Checked,
      );

      setSelectedList({ selectedList: defaultDataSelectedList, useShareCheck });
    }
  }, [serviceData, setSelectedList, useShareCheck, mainSkuId]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    // 防止重复处理相同的选择
    const isCurrentlySelected = selectedList.some((item) => item.skuId === value);
    const newSelected = isCurrentlySelected
      ? selectedList.filter((item) => item.skuId !== value) // 移除选中项
      : (() => {
          const toAdd = serviceData.serviceItems.find((item) => item.skuId === value);
          return toAdd ? [...selectedList, toAdd] : selectedList; // 添加新项
        })();

    // 如果选择状态没有变化，直接返回
    if (
      newSelected.length === selectedList.length &&
      newSelected.every((item) => selectedList.some((selected) => selected.skuId === item.skuId))
    ) {
      return;
    }

    // 处理埋点
    const trackItem = isCurrentlySelected
      ? selectedList.find((item) => item.skuId === value)
      : serviceData.serviceItems.find((item) => item.skuId === value);

    if (trackItem) {
      const { skuId, landedPriceText, crossPriceText } = trackItem;
      const sendName = isDoubleCheck
        ? 'Productdetail_AdditionalServiceDoubleCheck_Click'
        : 'Productdetail_AdditionalService_Click';
      sendClickTrack[sendName]({
        mainskuid: mainSkuId,
        serviceFloorBuryPoint: {
          skuid: skuId,
          firprice: landedPriceText,
          secprice: crossPriceText,
        },
        status: isCurrentlySelected ? '0' : '1',
      });
    }
    // @ts-ignore
    setSelectedList({ selectedList: newSelected, useShareCheck });
  };

  const goDetail = (e: React.MouseEvent, serviceItem: ServiceItem) => {
    const { skuId } = serviceItem || {};
    e.stopPropagation();
    sendClickTrack.Productdetail_AdditionalServiceDetails_Click({
      mainskuid: mainSkuId,
      serviceFloorBuryPoint: { skuid: skuId },
    });
    setDetailConfig({
      isOpen: true,
      serviceItem: serviceItem,
    });
  };

  const drawerChange = () => {
    setDetailConfig({
      isOpen: false,
    });
  };

  return serviceData?.serviceItems?.length ? (
    <div className={cn(styles.serviceListWrapper, wrapperClassName)}>
      <>
        {serviceData?.serviceItems?.map?.((item) => {
          const { name, skuId, landedPriceText, crossPriceText, statusText, enable } = item || {};

          return (
            <div
              className={styles.serviceItem}
              key={skuId}
              exptag={`exp|${
                isDoubleCheck
                  ? EXPOSURE_RECORD.Productdetail_AdditionalServiceDoubleCheck_Expo
                  : EXPOSURE_RECORD.Productdetail_AdditionalService_Expo
              }`}
              data-exptag-config={dataExpTagConfig}
              data-exptag-json-param={JSON.stringify({
                mainskuid: mainSkuId,
                serviceFloorBuryPoint: JSON.stringify({
                  skuid: skuId,
                  firprice: landedPriceText,
                  secprice: crossPriceText,
                }),
              })}
              // style={{ width: "100%", margin: "-2px 0 36px -2px" }}
            >
              <Checkbox
                checked={selectedList?.filter((i) => i.skuId === skuId)?.length > 0}
                onChange={handleChange}
                icon={<BpIcon />}
                value={skuId}
                color='primary'
                sx={{
                  padding: 0,
                  color: enable === EnableStatus.DISABLED ? '#E0E1E5' : '#888B94',
                  opacity: enable === EnableStatus.DISABLED ? 0.4 : 1, // 无货时降低透明度置灰
                  '&.Mui-checked': {
                    color: '#1A1A1A',
                  },
                }}
                disabled={enable === EnableStatus.DISABLED}
                disableRipple
              />
              <div className={styles.serviceLabelBox}>
                <span
                  className={cn(styles.serviceText, {
                    [styles.serviceTextDisabled]: enable === EnableStatus.DISABLED,
                  })}
                >
                  {name}
                </span>
                {/* 原价 */}
                {crossPriceText && (
                  <JDIPrice
                    locale={localInfo?.languageCode}
                    currency={localInfo?.currency}
                    price={crossPriceText || ''}
                    uiType={2}
                    containerStyle={{
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      height: '20px',
                      color: '#888B94',
                      textDecoration: 'line-through',
                      marginRight: '4px',
                    }}
                  />
                )}
                {/* 展示的价格 */}
                {!statusText && landedPriceText && (
                  <JDIPrice
                    locale={localInfo?.languageCode}
                    currency={localInfo?.currency}
                    price={landedPriceText || ''}
                    uiType={2}
                    containerStyle={{
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '20px',
                      height: '20px',
                      color: enable === EnableStatus.DISABLED ? '#888B94' : '#1A1A1A',
                    }}
                  />
                )}
                {/* 免费标签 */}
                {statusText && (
                  <RichComponent data={statusText} className={styles.free} isShadowDom={false} />
                )}
              </div>
              <div
                className={styles.arrowRightBox}
                onClick={(e: React.MouseEvent) => goDetail(e, item)}
              >
                <ArrowIcon className={styles.arrowRight} style={{}} />
              </div>
            </div>
          );
        })}
      </>
      <ServiceDetailDraw
        detailConfig={detailConfig}
        drawerChange={drawerChange}
        mainskuid={mainSkuId}
      />
    </div>
  ) : null;
};

export default observer(ServiceList);
